import { definePreset } from '@primevue/themes'
import Aura from '@primevue/themes/aura'

/**
 * HudStore Custom PrimeVue Preset
 *
 * Burgundy Wine + True Black palette:
 * - Light Primary: #8B1E3F (Burgundy Wine - elegant, sophisticated)
 * - Dark Primary: #A85A6A (Muted Rose - softer for true black)
 * - Light surfaces: Clean whites and light grays
 * - Dark surfaces: True black (#000000) with subtle elevation
 * - Complementary: Gold (#D4A853) + Copper (#B87333)
 */
const HudStorePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: '#FDF2F4',
      100: '#FCE7EB',
      200: '#F9CED6',
      300: '#F2A3B3',
      400: '#E06B85',
      500: '#8B1E3F',
      600: '#6D1730',
      700: '#4A0F20',
      800: '#320A16',
      900: '#1A050B',
      950: '#0D0205'
    },
    colorScheme: {
      light: {
        primary: {
          color: '#8B1E3F',
          inverseColor: '#ffffff',
          hoverColor: '#6D1730',
          activeColor: '#4A0F20'
        },
        highlight: {
          background: 'rgba(139, 30, 63, 0.1)',
          focusBackground: 'rgba(139, 30, 63, 0.2)',
          color: '#8B1E3F',
          focusColor: '#6D1730'
        },
        surface: {
          0: '#ffffff',
          50: '#FAFAFA',
          100: '#F5F5F5',
          200: '#E5E5E5',
          300: '#D4D4D4',
          400: '#A3A3A3',
          500: '#737373',
          600: '#525252',
          700: '#404040',
          800: '#262626',
          900: '#171717',
          950: '#0A0A0A'
        },
        formField: {
          background: '#ffffff',
          disabledBackground: '#F5F5F5',
          filledBackground: '#FAFAFA',
          filledHoverBackground: '#F5F5F5',
          filledFocusBackground: '#ffffff',
          borderColor: 'rgba(163, 163, 163, 0.3)',
          hoverBorderColor: '#A3A3A3',
          focusBorderColor: '#8B1E3F',
          invalidBorderColor: '#DC2626',
          color: '#171717',
          disabledColor: '#A3A3A3',
          placeholderColor: '#A3A3A3',
          invalidPlaceholderColor: '#DC2626',
          floatLabelColor: '#737373',
          floatLabelFocusColor: '#8B1E3F',
          floatLabelActiveColor: '#525252',
          floatLabelInvalidColor: '#DC2626',
          iconColor: '#737373',
          shadow: 'none'
        },
        text: {
          color: '#171717',
          hoverColor: '#0A0A0A',
          mutedColor: '#737373',
          hoverMutedColor: '#525252'
        },
        content: {
          background: '#ffffff',
          hoverBackground: '#FAFAFA',
          borderColor: 'rgba(163, 163, 163, 0.2)',
          color: '#171717',
          hoverColor: '#0A0A0A'
        },
        overlay: {
          select: {
            background: '#ffffff',
            borderColor: 'rgba(163, 163, 163, 0.2)',
            color: '#171717'
          },
          popover: {
            background: '#ffffff',
            borderColor: 'rgba(163, 163, 163, 0.2)',
            color: '#171717'
          },
          modal: {
            background: '#ffffff',
            borderColor: 'rgba(163, 163, 163, 0.2)',
            color: '#171717'
          }
        },
        navigation: {
          item: {
            focusBackground: 'rgba(139, 30, 63, 0.1)',
            activeBackground: 'rgba(139, 30, 63, 0.15)',
            color: '#171717',
            focusColor: '#8B1E3F',
            activeColor: '#8B1E3F',
            icon: {
              color: '#737373',
              focusColor: '#8B1E3F',
              activeColor: '#8B1E3F'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '#525252'
          },
          submenuIcon: {
            color: '#737373',
            focusColor: '#8B1E3F',
            activeColor: '#8B1E3F'
          }
        }
      },
      dark: {
        primary: {
          color: '#A85A6A',
          inverseColor: '#000000',
          hoverColor: '#8B4D5C',
          activeColor: '#6D3D4A'
        },
        highlight: {
          background: 'rgba(168, 90, 106, 0.15)',
          focusBackground: 'rgba(168, 90, 106, 0.25)',
          color: '#A85A6A',
          focusColor: '#C08090'
        },
        surface: {
          0: '#F5F5F5',
          50: '#E5E5E5',
          100: '#C4C4C4',
          200: '#A3A3A3',
          300: '#8C8C8C',
          400: '#525252',
          500: '#404040',
          600: '#292929',
          700: '#1A1A1A',
          800: '#0A0A0A',
          900: '#050505',
          950: '#000000'
        },
        formField: {
          background: '#0A0A0A',
          disabledBackground: '#141414',
          filledBackground: '#141414',
          filledHoverBackground: '#1F1F1F',
          filledFocusBackground: '#0A0A0A',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          hoverBorderColor: 'rgba(255, 255, 255, 0.2)',
          focusBorderColor: '#A85A6A',
          invalidBorderColor: '#F87171',
          color: '#F5F5F5',
          disabledColor: '#737373',
          placeholderColor: '#737373',
          invalidPlaceholderColor: '#F87171',
          floatLabelColor: '#A3A3A3',
          floatLabelFocusColor: '#A85A6A',
          floatLabelActiveColor: '#C4C4C4',
          floatLabelInvalidColor: '#F87171',
          iconColor: '#A3A3A3',
          shadow: 'none'
        },
        text: {
          color: '#F5F5F5',
          hoverColor: '#ffffff',
          mutedColor: '#A3A3A3',
          hoverMutedColor: '#C4C4C4'
        },
        content: {
          background: '#0A0A0A',
          hoverBackground: '#141414',
          borderColor: 'rgba(255, 255, 255, 0.1)',
          color: '#F5F5F5',
          hoverColor: '#ffffff'
        },
        overlay: {
          select: {
            background: '#141414',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            color: '#F5F5F5'
          },
          popover: {
            background: '#141414',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            color: '#F5F5F5'
          },
          modal: {
            background: '#141414',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            color: '#F5F5F5'
          }
        },
        navigation: {
          item: {
            focusBackground: 'rgba(168, 90, 106, 0.15)',
            activeBackground: 'rgba(168, 90, 106, 0.2)',
            color: '#F5F5F5',
            focusColor: '#A85A6A',
            activeColor: '#A85A6A',
            icon: {
              color: '#A3A3A3',
              focusColor: '#A85A6A',
              activeColor: '#A85A6A'
            }
          },
          submenuLabel: {
            background: 'transparent',
            color: '#A3A3A3'
          },
          submenuIcon: {
            color: '#A3A3A3',
            focusColor: '#A85A6A',
            activeColor: '#A85A6A'
          }
        }
      }
    }
  }
})

export default HudStorePreset
